"use client"

import React, { useEffect, useMemo, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { getStoryBlokStoryByPath } from "@/actions/storyblokActions"
import { resolveStoryblokStoryHref } from "@/lib/link-utils/storyblok-link-resolver"

import { ReducedProductData } from "@/lib/storefront/product/product"
import { trackBannerClick, trackGTMAndFF } from "@/lib/tracking/tracking-helper"
import { cn } from "@/components/ui/helpers/utils"
import HtmlTextBox from "@/components/storyblok/common/HtmlTextBox"
import { FactFinderBannerStoryblok } from "@/components/storyblok/component-types"

interface BannerClientProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  bannerContent: string
  bannerPosition: number | undefined
  bannerCampaignId: string | undefined
  isListView: boolean
}

export const BannerClient: React.FC<BannerClientProps> = ({
  bannerContent,
  bannerPosition,
  bannerCampaignId,
  isListView,
  className,
  style,
  ...props
}) => {
  const isNewStyle = bannerContent.trim().startsWith("fact-finder-banners/")
  const [sbStory, setSBStory] = useState<FactFinderBannerStoryblok | undefined | null>(undefined)

  const fetchSBStory = async (content: string) => {
    const result = await getStoryBlokStoryByPath(content.trim())
    console.log("fetchSBStory1", result)
    if (result) {
      const story = result.data.story.content as FactFinderBannerStoryblok
      console.log("fetchSBStory2", story)
      if (story) {
        setSBStory(story)
        return
      }
    }
    setSBStory(null)
    return
  }

  useEffect(() => {
    if (bannerContent.trim().startsWith("fact-finder-banners/")) {
      // noinspection JSIgnoredPromiseFromCall
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchSBStory(bannerContent)
    } else {
      setSBStory(undefined)
    }
  }, [bannerContent])

  return (
    <>
      {isNewStyle ? (
        sbStory?.default_image.filename && (
          <div
            style={{ ...style, backgroundImage: `url(${sbStory.default_image.filename})` }}
            className={cn(
              className,
              "align-center flex size-full justify-center rounded-lg border border-gray-300 bg-cover bg-center"
            )}
            {...props}
          >
            <Link
              href={
                sbStory.link_target.linktype == "story"
                  ? resolveStoryblokStoryHref(
                      sbStory.link_target.cached_url ?? sbStory.link_target.url ?? "",
                      undefined,
                      sbStory.link_target.story
                    )
                  : sbStory.link_target.url
              }
              target={sbStory.link_target.target}
              onClick={() => trackBannerClick(bannerCampaignId, bannerPosition)}
              className={"flex size-full items-center justify-center overflow-hidden rounded-lg backdrop-blur-sm"}
            >
              {/* TODO use <StoryblockAsset> */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={cn("size-fit object-contain")}
                src={sbStory.default_image.filename ?? ""}
                alt={sbStory.default_image.alt ?? ""}
              />
            </Link>
          </div>
        )
      ) : (
        <>
          <div
            style={{ backgroundColor: "#1c3643", ...style }}
            className={cn(
              className,
              `
                flex size-full justify-center rounded-lg border border-gray-300 bg-white
                prose-img:m-0 prose-img:rounded-lg
              `,
              { "@lg:hidden": isListView }
            )}
            {...props}
          >
            <HtmlTextBox text={bannerContent ?? ""} />
          </div>
        </>
      )}
    </>
  )
}
